// src/pages/Admin/CustomerManagement.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Plus, RefreshCw, Building2, ChevronDown } from 'lucide-react';
import AdminLayout from '@/components/Admin/Layout/AdminLayout';
import ModuleLayout from '@/layouts/ModuleLayout';
import CustomerList from '@/components/Admin/Customer_Master/CustomerView';
import { useAuth } from '@/context/AuthContext';
import { P } from '@/config/permissionCodes';
import branchService from '@/services/branchService';

/**
 * Customer Management Page
 *
 * Manage customer profiles:
 * - Add/Edit customers
 * - View customer details
 * - Track loyalty points
 */
export default function CustomerManagementPage() {
  const [componentActions, setComponentActions] = useState(null);
  const location = useLocation();
  const { user, hasPermission } = useAuth();


  const isOwner = user?.userType === 'SUPER_USER';
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);

  // Permission flags
  const canAdd    = hasPermission(P.CRM.CUSTOMERS.ADD);
  const canView   = hasPermission(P.CRM.CUSTOMERS.SCREEN);

  // Detect if accessed from CRM module
  const isCRMRoute = location.pathname.startsWith('/crm');
  const Layout = isCRMRoute ? ModuleLayout : AdminLayout;

  useEffect(() => {
    if (!isOwner) return;
    branchService.getAllBranches().then(result => {
      if (result && result.length > 1) setBranches(result);
    }).catch(console.error);
  }, [isOwner]);

  const handleActionsReady = useCallback((actions) => {
    setComponentActions(actions);
  }, []);

  // Guard: no view permission
  if (!canView) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full text-center px-6 py-20">
          <div>
            <p className="text-lg font-semibold text-gray-700">Access Denied</p>
            <p className="text-sm text-gray-500 mt-1">You don't have permission to view customers.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-col h-full">
        {/* Sticky Header */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <div>
            <h1 className="text-lg font-bold text-slate-900">Customer Management</h1>
            <p className="text-xs text-slate-500">Manage customer profiles and loyalty</p>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {/* Branch Dropdown for SUPER_USER */}
            {branches.length > 1 && (
              <div className="relative">
                <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <select
                  value={selectedBranchId ?? ''}
                  onChange={(e) => setSelectedBranchId(e.target.value || null)}
                  className="pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 appearance-none cursor-pointer"
                >
                  <option value="">All Branches</option>
                  {branches.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            )}

            {componentActions && (
              <>
                <button
                  onClick={componentActions.onRefresh}
                  className="p-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"
                >
                  <RefreshCw size={16} />
                </button>

                {/* Only render Add button if user has CRM.CUSTOMER.ADD */}
                {canAdd && (
                  <button
                    onClick={componentActions.onAdd}
                    className="px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-1.5 text-sm font-medium"
                  >
                    <Plus size={16} />
                    Add Customer
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <CustomerList
            renderActions={handleActionsReady}
            selectedBranchId={selectedBranchId}
            isOwner={isOwner}
          />
        </div>
      </div>
    </Layout>
  );
}